import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Plus, ChevronRight, CheckCircle2 } from "lucide-react";
import Layout from '../Components/Layout/Layout';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import StyledSwitch from "../Components/Layout/StyledSwitch";
import axiosInstance from '../utils/axiosConfig';
import DishModal from "../Components/Categories/DishModal";
import ConfirmationModal from '../Components/Includes/ConfirmationModal';

const Category = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { categoryId } = location.state || {};
    const [isDishModalOpen, setIsDishModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null); 
    const [success, setSuccess] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [category, setCategory] = useState({
        id: null,
        name: '',
        description: '', 
        ispublished: true,
        dishes: []
    });
    const [isSaving, setIsSaving] = useState(false); 
    const [nameError, setNameError] = useState('');

    const handleReturn = () => {
        navigate(`/categories`);
    };

    const handleReOpen = (newId) => {
        navigate('/category', {
            state: {
                categoryId: newId
            }
        });
    };

    const validateForm = () => {
        let isValid = true;
        setNameError('');

        if (!category.name.trim()) {
            setNameError(t('validation.nameRequired'));
            isValid = false;
        }

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            const response = await axiosInstance.post('/category/SetCategory', {
                id: category.id || 0,
                name: category.name,
                description: category.description,
                ispublished: category.ispublished,  
                dishes: category.dishes || [],  
                count: category.count || 0 
            });
            const data = await response.data; 
             
            if (response.status !== 200) {
                throw new Error('Failed to save category');
            } else {
                setShowSuccess(true);
                setSuccess(t('categories.saved'));

                setTimeout(() => {
                    setShowSuccess(false);
                    if (categoryId === 0) {
                        handleReOpen(data);
                    } else {
                        handleReOpen(categoryId);
                    }
                }, 2000); 
            }
        } catch (error) {
            console.error('Error saving category:', error);
            setError(error.message);
        } finally {
            setIsSaving(false);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }
    };

    const handleAddDish = () => {
        setIsDishModalOpen(true);
    };

    const handleSaveDish = async (dish) => {
        try {
            await axiosInstance.put(`/category/AddDish/${categoryId}/${dish.id}`);

            setCategory(prev => ({
                ...prev,
                dishes: [...(prev.dishes || []), dish]
            }));
        } catch (error) {
            console.error('Error adding dish:', error);
        }
    };

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                setIsLoading(true);
                const response = await axiosInstance.get(`/category/GetCategory/${categoryId}`);
                const data = await response.data; 
                setCategory(data); 
            } catch (error) {
                console.error('Error fetching category:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        if (categoryId && categoryId !== 0) {
            fetchCategory();
        } else {
            setIsLoading(false);
        }
    }, [categoryId]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setCategory(prevState => ({
            ...prevState,
            [name]: value
        }));

        if (name === 'name') {
            setNameError('');
        }
    }, []);

    const handleSwitchChange = (checked) => {
        setCategory(prevState => ({
            ...prevState,
            ispublished: checked
        }));
    };

    const handleDishClick = (dishId) => {
        navigate('/dish', {
            state: {
                dishId: dishId,
                returnPage: 'category',
                categoryId: categoryId
            }
        });
    };

    const handleDeleteCategory = async () => {
        try {
            await axiosInstance.delete(`/category/DeleteCategory/${categoryId}`);
            navigate('/categories');
        } catch (error) {
            console.error('Error deleting category:', error);
            setError(error.message);
        }
    };
    
    if (isLoading) return (
        <Layout>
            <div>{t('common.loading')}</div>
        </Layout>
    );
    
    if (error) return (
        <Layout>
            <div>{t('common.error')}: {error}</div>
        </Layout>
    );
    
    if (!category && categoryId !== 0) return (
        <Layout>
            <div>{t('categories.categorynotfound')}</div>
        </Layout>
    );

    return (
        <Layout>
            <div className="relative max-w-[1100px] mx-auto">
                <div className="mb-8">
                    <button
                        onClick={handleReturn}
                        className="text-ps-blue-dark hover:underline font-inter"
                    >
                        <ChevronLeft className="float-left" />
                        {t('common.backToOverview')}
                    </button>
                </div>

                {showSuccess && (
                    <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-inter">{success}</span>
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
                        <span className="font-inter">{error}</span>
                    </div>
                )}

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                            {t('categories.name')}
                        </label>
                        <Input
                            type="text"
                            name="name"
                            value={category.name}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded bg-white ${nameError ? 'border-red-500' : ''}`}
                        />
                        {nameError && (
                            <p className="text-red-500 text-sm mt-1 font-inter">
                                {nameError}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                            {t('categories.description')}
                        </label>
                        <textarea
                            name="description"
                            value={category.description}
                            onChange={handleChange}
                            className="w-full p-2 border rounded h-32 resize-none font-inter bg-white"
                        />
                    </div>

                    <div className="flex items-center space-x-2 py-0">
                        <StyledSwitch
                            title={t('categories.publish')}
                            checked={category.ispublished}
                            onCheckedChange={handleSwitchChange}
                            id="publish-mode"
                        /> 
                    </div>

                    <div className="flex justify-end">
                        {(category && categoryId > 0 && category.dishes.length === 0) ? (
                            <Button
                                variant="destructive"
                                onClick={() => setIsDeleteModalOpen(true)}
                                className="flex gap-2 w-60 bg-red-700 text-white hover:opacity-80 mr-2"
                            >
                                {t('common.delete')}
                            </Button>
                        ) : (
                            <Button
                                variant="destructive"
                                disabled="true"
                                className="flex gap-2 w-60 bg-gray-400 text-white hover:opacity-80 mr-2"
                            >
                                {t('common.delete')}
                            </Button>
                        )}
                        <Button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="bg-ps-blue-dark hover:bg-ps-blue-darker text-white w-60"
                        >
                            {isSaving ? t('common.saving') : t('common.save')}
                        </Button>
                    </div>
                    
                    <ConfirmationModal
                        isOpen={isDeleteModalOpen}
                        onClose={() => setIsDeleteModalOpen(false)}
                        onConfirm={handleDeleteCategory}
                        title={t('categories.deleteCategory')}
                        message={t('categories.confirmDelete')}
                    />

                    {categoryId !== 0 && (
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-4">
                                {t('categories.dishes')}
                            </label>

                            <div className="space-y-2">
                                {category.dishes && category.dishes.map((dish) => (
                                    <div
                                        key={dish.id}
                                        className="flex items-center justify-between w-full p-3 hover:pl-6 cursor-pointer border-b-[1px]"
                                        onClick={() => handleDishClick(dish.id)}
                                    >
                                        <span className="font-inter">{dish.name}</span>
                                        <div className="flex items-center gap-6">
                                            <div className="flex text-black font-inter">
                                                <span className="mr-1">&euro;</span>
                                                <span className="w-12 text-right">{dish.price.toFixed(2)}</span>
                                            </div>
                                            <ChevronRight className="text-ps-blue-dark" size={20} />
                                        </div>
                                    </div>
                                ))}
                                <div className="flex justify-end mt-4">
                                    <Button
                                        onClick={handleAddDish}
                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-green hover:bg-ps-green/90 text-white px-4 py-2 shadow-lg rounded-full"
                                    >
                                        <Plus className="h-6 w-6" />
                                        <span>{t('categories.addDish')}</span>
                                    </Button>
                                    <DishModal
                                        isOpen={isDishModalOpen}
                                        onClose={() => setIsDishModalOpen(false)}
                                        onSave={handleSaveDish}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Category;